import { useEffect, useRef, useState } from "react";

import { ContextMenuItem } from "@/components/ui/context-menu";

const TableTheme: React.FC<
  React.ComponentProps<"div"> & {
    rightClickedElement: HTMLElement;
  }
> = ({ rightClickedElement }) => {
  const [backgroundColor, setBackgroundColor] = useState("#ffffff");
  const [textColor, setTextColor] = useState("#000000");
  const cleanupRowListenersRef = useRef<(() => void) | null>(null);
  const textColorRef = useRef(textColor);

  const colorStringToHex = (colorString: string): string => {
    const normalizedColor = colorString.trim();

    if (normalizedColor.startsWith("#")) {
      return normalizedColor.length === 4
        ? `#${normalizedColor[1]}${normalizedColor[1]}${normalizedColor[2]}${normalizedColor[2]}${normalizedColor[3]}${normalizedColor[3]}`
        : normalizedColor;
    }

    const rgbArray = normalizedColor.match(/\d+/g);

    if (!rgbArray || rgbArray.length < 3) {
      return "#ffffff";
    }

    const [r, g, b] = rgbArray.map(Number);

    const toHex = (n: number) => {
      const hex = Math.min(255, Math.max(0, n)).toString(16).padStart(2, "0");
      return hex;
    };

    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  };

  const hexToRgb = (hexColor: string) => {
    const normalizedColor = colorStringToHex(hexColor).replace("#", "");
    const red = parseInt(normalizedColor.slice(0, 2), 16);
    const green = parseInt(normalizedColor.slice(2, 4), 16);
    const blue = parseInt(normalizedColor.slice(4, 6), 16);

    return { red, green, blue };
  };

  const rgbToHex = (red: number, green: number, blue: number) => {
    const toHex = (value: number) =>
      Math.min(255, Math.max(0, value)).toString(16).padStart(2, "0");

    return `#${toHex(red)}${toHex(green)}${toHex(blue)}`;
  };

  const lightenColor = (hexColor: string, amount: number) => {
    const { red, green, blue } = hexToRgb(hexColor);

    return rgbToHex(
      red + (255 - red) * amount,
      green + (255 - green) * amount,
      blue + (255 - blue) * amount
    );
  };

  const applyColorToElements = (
    elements: Element[],
    background: string,
    color?: string
  ) => {
    elements.forEach((element) => {
      const htmlElement = element as HTMLElement;
      htmlElement.style.backgroundColor = background;

      if (color) {
        htmlElement.style.color = color;
      }
    });
  };

  const clearRowListeners = () => {
    if (cleanupRowListenersRef.current) {
      cleanupRowListenersRef.current();
      cleanupRowListenersRef.current = null;
    }
  };

  useEffect(() => {
    const initialBackgroundColor =
      rightClickedElement.style.backgroundColor ||
      getComputedStyle(rightClickedElement).backgroundColor ||
      "rgb(255, 255, 255)";

    const initialTextColor =
      rightClickedElement.style.color ||
      getComputedStyle(rightClickedElement).color ||
      "rgb(0, 0, 0)";

    setBackgroundColor(colorStringToHex(initialBackgroundColor));
    setTextColor(colorStringToHex(initialTextColor));

    return () => {
      clearRowListeners();
    };
  }, [rightClickedElement]);

  const clearLegacyTableThemeClasses = () => {
    rightClickedElement.classList.remove(
      "table-transparent",
      "table-black",
      "table-blue",
      "table-red",
      "table-green",
      "table-purple",
      "table-orange"
    );
  };

  const applyThemeColor = (value: string) => {
    clearLegacyTableThemeClasses();
    clearRowListeners();

    const hoverColor = lightenColor(value, 0.88);
    const stripeColor = lightenColor(value, 0.95);
    const currentTextColor = textColorRef.current;

    const explicitHeaderElements = Array.from(
      rightClickedElement.querySelectorAll(
        "thead, thead tr, thead th, thead td, caption"
      )
    );

    const allRows = Array.from(rightClickedElement.querySelectorAll("tr"));
    const fallbackHeaderRow =
      explicitHeaderElements.length === 0
        ? allRows.find((row) => row.querySelector("th"))
        : null;

    if (fallbackHeaderRow) {
      applyColorToElements([fallbackHeaderRow], value, currentTextColor);
      const fallbackHeaderCells = Array.from(
        fallbackHeaderRow.querySelectorAll("th, td")
      );
      applyColorToElements(fallbackHeaderCells, value, currentTextColor);
    } else {
      applyColorToElements(explicitHeaderElements, value, currentTextColor);
    }

    const bodyRows = fallbackHeaderRow
      ? allRows.filter((row) => row !== fallbackHeaderRow)
      : allRows;

    const cleanupHandlers: Array<() => void> = [];

    bodyRows.forEach((row, rowIndex) => {
      const rowBackgroundColor = rowIndex % 2 === 0 ? value : stripeColor;
      const cells = Array.from(row.querySelectorAll("td, th"));

      applyColorToElements(
        [row, ...cells],
        rowBackgroundColor,
        currentTextColor
      );

      const handleMouseEnter = () => {
        applyColorToElements([row, ...cells], hoverColor, textColorRef.current);
      };

      const handleMouseLeave = () => {
        applyColorToElements(
          [row, ...cells],
          rowBackgroundColor,
          textColorRef.current
        );
      };

      row.addEventListener("mouseenter", handleMouseEnter);
      row.addEventListener("mouseleave", handleMouseLeave);

      cleanupHandlers.push(() => {
        row.removeEventListener("mouseenter", handleMouseEnter);
        row.removeEventListener("mouseleave", handleMouseLeave);
      });
    });

    cleanupRowListenersRef.current = () => {
      cleanupHandlers.forEach((cleanupHandler) => cleanupHandler());
    };

    setBackgroundColor(value);
  };

  const applyTextColor = (value: string) => {
    rightClickedElement.style.color = value;
    textColorRef.current = value;

    const elements = rightClickedElement.querySelectorAll("*");
    elements.forEach((element) => {
      (element as HTMLElement).style.color = value;
    });

    setTextColor(value);
  };

  return (
    <>
      <ContextMenuItem className="p-0">
        <label
          className="flex w-full items-center justify-between px-2 py-1.5"
          onClick={(event) => event.stopPropagation()}
        >
          رنگ
          <span
            className="block size-5 rounded-full border border-slate-300"
            style={{ backgroundColor }}
          ></span>
          <input
            type="color"
            className="absolute size-0 cursor-pointer opacity-0"
            value={backgroundColor}
            onChange={(event) => {
              applyThemeColor(event.target.value);
            }}
          />
        </label>
      </ContextMenuItem>

      <ContextMenuItem className="p-0">
        <label
          className="flex w-full items-center justify-between px-2 py-1.5"
          onClick={(event) => event.stopPropagation()}
        >
          رنگ متن
          <span
            className="block size-5 rounded-full border border-slate-300"
            style={{ backgroundColor: textColor }}
          ></span>
          <input
            type="color"
            className="absolute size-0 cursor-pointer opacity-0"
            value={textColor}
            onChange={(event) => {
              applyTextColor(event.target.value);
            }}
          />
        </label>
      </ContextMenuItem>
    </>
  );
};

export default TableTheme;
